import React, { useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const CIIRatingTracker = ({ vessel, ciiData }) => {
  const [viewMode, setViewMode] = useState('monthly');

  const ratingColors = {
    'A': '#22c55e', // success
    'B': '#84cc16', // lime
    'C': '#eab308', // warning
    'D': '#f97316', // orange
    'E': '#ef4444'  // error
  };

  const ratingLabels = {
    'A': 'Superior',
    'B': 'Minor',
    'C': 'Moderate',
    'D': 'Major',
    'E': 'Inferior'
  };

  const chartData = ciiData?.map(item => ({
    period: item?.period,
    rating: item?.rating,
    value: item?.ciiValue,
    threshold: item?.threshold,
    target: item?.target
  }));

  const currentRating = ciiData?.[ciiData?.length - 1]?.rating || 'C';
  const currentValue = ciiData?.[ciiData?.length - 1]?.ciiValue || 0;
  const trend = ciiData?.length > 1 ? 
    (ciiData?.[ciiData?.length - 1]?.ciiValue - ciiData?.[ciiData?.length - 2]?.ciiValue) : 0;

  const getRatingColor = (rating) => ratingColors?.[rating] || ratingColors?.['C'];

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload?.length) {
      const data = payload?.[0]?.payload;
      return (
        <div className="bg-popover border border-border rounded-lg p-3 shadow-maritime-md">
          <p className="text-sm font-medium text-foreground mb-2">{label}</p>
          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">CII Value:</span>
              <span className="text-sm font-medium text-foreground">
                {data?.value?.toFixed(2)}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Rating:</span>
              <span 
                className="text-sm font-bold px-2 py-1 rounded"
                style={{ 
                  backgroundColor: getRatingColor(data?.rating) + '20',
                  color: getRatingColor(data?.rating)
                }}
              >
                {data?.rating} - {ratingLabels?.[data?.rating]}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Threshold:</span>
              <span className="text-sm font-medium text-foreground">
                {data?.threshold?.toFixed(2)}
              </span>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-card rounded-lg border p-6 shadow-maritime-sm">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-foreground">CII Rating Tracker</h3>
          <p className="text-sm text-muted-foreground">Carbon Intensity Indicator performance over time</p>
        </div>
        
        <div className="flex items-center space-x-3">
          <Button
            variant={viewMode === 'monthly' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setViewMode('monthly')}
          >
            Monthly
          </Button>
          <Button
            variant={viewMode === 'quarterly' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setViewMode('quarterly')}
          >
            Quarterly
          </Button>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <div className="lg:col-span-2">
          <div className="h-64 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis 
                  dataKey="period" 
                  stroke="var(--color-muted-foreground)"
                  fontSize={12}
                />
                <YAxis 
                  stroke="var(--color-muted-foreground)"
                  fontSize={12}
                  label={{ value: 'CII Value', angle: -90, position: 'insideLeft' }}
                />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                  {chartData?.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={getRatingColor(entry?.rating)} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="space-y-4">
          <div className="p-4 rounded-lg border bg-background">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-muted-foreground">Current Rating</span>
              <div className={`flex items-center space-x-2 px-3 py-1 rounded-full text-sm font-bold`}
                   style={{ 
                     backgroundColor: getRatingColor(currentRating) + '20',
                     color: getRatingColor(currentRating)
                   }}>
                <span>{currentRating}</span>
                <span>•</span>
                <span>{ratingLabels?.[currentRating]}</span>
              </div>
            </div>
            <div className="text-2xl font-bold text-foreground">
              {currentValue?.toFixed(2)}
            </div>
            <div className={`flex items-center space-x-1 text-sm ${
              trend < 0 ? 'text-success' : trend > 0 ? 'text-error' : 'text-muted-foreground'
            }`}>
              <Icon 
                name={trend < 0 ? 'TrendingDown' : trend > 0 ? 'TrendingUp' : 'Minus'} 
                size={14} 
                strokeWidth={2} 
              />
              <span>{Math.abs(trend)?.toFixed(2)} from last period</span>
            </div>
          </div>

          <div className="p-4 rounded-lg border bg-background">
            <div className="flex items-center space-x-2 mb-3">
              <Icon name="Target" size={16} className="text-accent" strokeWidth={2} />
              <span className="text-sm font-medium text-foreground">Rating Thresholds</span>
            </div>
            <div className="space-y-2">
              {Object.entries(ratingLabels)?.map(([rating, label]) => (
                <div key={rating} className="flex items-center justify-between text-sm">
                  <div className="flex items-center space-x-2">
                    <div 
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: getRatingColor(rating) }}
                    ></div>
                    <span className="font-medium">{rating}</span>
                    <span className="text-muted-foreground">{label}</span>
                  </div>
                  {rating === currentRating && (
                    <Icon name="ArrowRight" size={14} className="text-accent" strokeWidth={2} />
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="p-4 rounded-lg border bg-warning/5 border-warning/20">
            <div className="flex items-center space-x-2 mb-2">
              <Icon name="AlertTriangle" size={16} className="text-warning" strokeWidth={2} />
              <span className="text-sm font-medium text-foreground">Risk Assessment</span>
            </div>
            <p className="text-sm text-muted-foreground">
              {currentRating === 'D' || currentRating === 'E' 
                ? "Immediate action required to improve rating"
                : currentRating === 'C'
                ? "Monitor closely to prevent rating decline" :"Performance within acceptable range"
              }
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CIIRatingTracker;